import { ArrowDownLeft, ArrowUpRight, Loader2, Receipt } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { listLedger } from '@/api/member';
import Pagination from '@/components/member/Pagination';

const typeColor = (t) => {
  const map = {
    Deposit: '#7dd87a',
    Withdrawal: '#ff8a8a',
    Cycle: '#FFC20E',
    Match: '#FFB612',
    PIF: '#FFE08A',
    InternalTransfer: '#9bb8ff',
    SelfWalletExchange: '#9bb8ff',
    // Import-time balance corrections (the owner's zero-loss rule).
    DepositCorrection: '#ff8a8a',
    MatchCorrection: '#ff8a8a',
  };
  return map[t] || '#f7f7f8';
};

const LedgerSubpage = () => {
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listLedger(page);
      setData(r);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-6" data-testid="subpage-ledger">
      <section className="sc-glass-strong rounded-2xl p-5 md:p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="sc-pill">
              <span className="sc-dot" />
              Transaction History
            </div>
            <h3 className="font-display font-bold text-xl md:text-2xl mt-3">
              Your Complete <span className="sc-gold-text">Ledger</span>
            </h3>
            <p className="text-[13px] text-[var(--sc-text-dim)] mt-1">
              Crypto deposits, payouts, cycle returns, matching bonuses, internal transfers, and PIF outlays. 50 rows per page.
            </p>
          </div>
          <div className="hidden md:flex items-center gap-2 text-[var(--sc-text-mute)] text-[11px] uppercase tracking-[0.22em]">
            <Receipt className="w-3.5 h-3.5" />
            {data?.total ?? 0} entries
          </div>
        </div>

        {loading ? (
          <div className="p-8 flex items-center justify-center text-[var(--sc-text-dim)] text-sm">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
          </div>
        ) : !data?.items?.length ? (
          <div className="p-10 text-center text-[var(--sc-text-dim)] text-sm">
            No ledger entries yet. Make your first deposit or position purchase to populate this view.
          </div>
        ) : (
          <div className="overflow-x-auto -mx-2 md:mx-0">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)] border-b border-[rgba(255,182,18,0.10)]">
                  <th className="text-left font-medium py-3 px-3">Timestamp</th>
                  <th className="text-left font-medium py-3 px-3">Type</th>
                  <th className="text-right font-medium py-3 px-3">Amount (USD)</th>
                  <th className="text-left font-medium py-3 px-3 hidden lg:table-cell">Notes</th>
                  <th className="text-left font-medium py-3 px-3 hidden xl:table-cell">Txn ID</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((row) => {
                  const amt = Number(row.amount);
                  const positive = amt >= 0;
                  return (
                    <tr
                      key={row.transaction_id}
                      className="border-b border-[rgba(255,182,18,0.05)] hover:bg-[rgba(255,182,18,0.025)] transition"
                      data-testid={`ledger-row-${row.transaction_id.slice(0, 6)}`}
                    >
                      <td className="py-2.5 px-3 sc-tabular text-[12px] text-[var(--sc-text-dim)]">{String(row.timestamp).replace('T', ' ').slice(0, 19)}</td>
                      <td className="py-2.5 px-3">
                        <span
                          className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.16em] font-semibold border"
                          style={{
                            color: typeColor(row.type),
                            borderColor: 'rgba(255,255,255,0.08)',
                            background: 'rgba(255,255,255,0.02)',
                          }}
                        >
                          {row.type}
                        </span>
                      </td>
                      <td className="py-2.5 px-3 sc-tabular text-right font-semibold">
                        <span className="inline-flex items-center gap-1" style={{ color: positive ? '#7dd87a' : '#ff8a8a' }}>
                          {positive ? <ArrowDownLeft className="w-3 h-3" /> : <ArrowUpRight className="w-3 h-3" />}
                          {positive ? '+' : ''}
                          {amt.toFixed(2)}
                        </span>
                      </td>
                      <td className="py-2.5 px-3 hidden lg:table-cell text-[11px] text-[var(--sc-text-mute)] max-w-[280px] truncate">
                        {summarizeMetadata(row.metadata)}
                      </td>
                      <td className="py-2.5 px-3 hidden xl:table-cell text-[10px] text-[var(--sc-text-mute)] font-mono">{row.transaction_id.slice(0, 12)}…</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {data && <Pagination page={data.page} totalPages={data.total_pages} total={data.total} onChange={setPage} />}
      </section>
    </div>
  );
};

function summarizeMetadata(md) {
  if (!md || typeof md !== 'object') return '';
  const parts = [];
  if (md.asset) parts.push(`asset=${md.asset}`);
  if (md.purpose) parts.push(md.purpose);
  if (md.fund_source) parts.push(`from=${md.fund_source}`);
  if (md.target_user_id) parts.push(`→ ${String(md.target_user_id).slice(0, 6)}`);
  if (md.sender_user_id) parts.push(`← ${String(md.sender_user_id).slice(0, 6)}`);
  if (md.reason) parts.push(md.reason);
  if (md.note) parts.push(`"${md.note}"`);
  return parts.join(' · ');
}

export default LedgerSubpage;
